import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { take } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';

import { MagicTokenService } from '@core/services/magic-token.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuth } from '@state/auth/auth.selectors';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-shared-iframe-modal',
  templateUrl: './iframe-modal.component.html',
  styleUrls: ['./iframe-modal.component.scss'],
})
export class IframeModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) pageSrc: string = '';
  @Input() pageSrcs: { title: string; src: string }[] | undefined;

  @Input() pageTitle: string | undefined;
  @Input() requireAuth = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() updateData = new EventEmitter<void>();

  protected magicToken: string | undefined;
  protected authenticating = this.requireAuth;
  protected safePageSrc: SafeResourceUrl | undefined;
  protected safePageSrcs: SafeResourceUrl[] | undefined;
  protected firstIframeLoaded = false;
  protected isLoggedIn = false;

  protected selectedPage = 0;
  protected showCloseModal = false;

  constructor(
    private magicTokenService: MagicTokenService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    window.addEventListener('message', this.handleIframeMessage.bind(this));

    this.authenticating = this.requireAuth;

    if (this.pageSrcs && this.pageSrcs?.length > 1) {
      this.selectPage(this.selectedPage);
    }

    console.log('pageSrcs', this.pageSrcs);

    this.store
      .select(selectAuth)
      .pipe(untilDestroyed(this))
      .subscribe((auth) => {
        this.isLoggedIn = !!auth.token && !!auth.user;
      });

    if (this.requireAuth) {
      this.magicTokenService
        .requestMagicToken()
        .pipe(take(1))
        .subscribe((token) => {
          this.magicToken = token.requestMagicToken;
          const finalUrl = this.pageSrc.includes('?')
            ? `${this.pageSrc}&magic_token=${this.magicToken}`
            : `${this.pageSrc}?magic_token=${this.magicToken}`;

          this.safePageSrc =
            this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);

          if (this.pageSrcs && this.pageSrcs.length > 1) {
            this.safePageSrcs = this.pageSrcs.map((page, index) => {
              const finalSrc =
                index === 0
                  ? page.src.includes('?')
                    ? `${page.src}&magic_token=${this.magicToken}`
                    : `${page.src}?magic_token=${this.magicToken}`
                  : page.src;
              return this.sanitizer.bypassSecurityTrustResourceUrl(finalSrc);
            });
            console.log('safePageSrcs', this.safePageSrcs);
          }

          this.authenticating = false;
          this.cdr.detectChanges();
        });
    } else {
      this.safePageSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.pageSrc
      );
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleIframeMessage.bind(this));
  }

  onFirstIframeLoad() {
    setTimeout(() => {
      this.firstIframeLoaded = true;
      this.cdr.markForCheck();
    }, 1000);
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('iframe-modal')) {
      this.showCloseModal = true;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    event.preventDefault();
    this.showCloseModal = !this.showCloseModal;
  }

  // Listen for enter and close the iframe if modal is open
  @HostListener('document:keydown.enter', ['$event'])
  onKeydownEnterHandler(event: KeyboardEvent) {
    event.preventDefault();
    if (this.showCloseModal) {
      this.closeModal.emit();
    }
  }

  // Call this code inside the iframe:
  // window.parent.postMessage({ type: 'closeAngularIframe' }, '*');
  // window.parent.postMessage({ type: 'forceCloseAngularIframe' }, '*');
  // window.parent.postMessage({ type: 'updateAngularData' }, '*');
  private handleIframeMessage(event: MessageEvent) {
    // Shows the close modal
    if (event.data?.type === 'closeAngularIframe') {
      this.showCloseModal = true;
      this.cdr.markForCheck();
    }

    // Force iframe modal close
    if (event.data?.type === 'forceCloseAngularIframe') {
      console.log('forceCloseAngularIframe', event.data);
      this.closeModal.emit();
    }

    // Calls updateData() method, which can by used to update data in the parent component
    if (event.data?.type === 'updateAngularData') {
      this.updateData.emit();
    }

    // If the iframe sends a message with a magic token, it will be used to sign in automatically
    if (!!event.data?.magicToken) {
      this.autoSignInWithMagicToken(
        event.data?.magicToken,
        event.data?.redirectTo
      );
    } else if (event.data?.redirectTo) {
      this.router.navigate([event.data?.redirectTo]);
    }
  }

  autoSignInWithMagicToken(
    magicToken: string,
    redirectTo: string | null = null
  ) {
    if (!this.isLoggedIn) {
      const originalUrl = this.router.url;
      this.router.navigate(['/auth/magic-token'], {
        queryParams: {
          token: magicToken,
          originalUrl: redirectTo || originalUrl,
        },
      });
    }
  }

  selectPage(i: number): void {
    this.selectedPage = i;
    this.cdr.markForCheck();
  }
}
